/**
 * Hook to get the recommended game for a user.
 *
 * This hook handles:
 * - Building game data for visible games across a 30-day recommendation window
 * - Registering recommendation scope in the global status store
 * - Optionally fetching statuses (or subscribing-only)
 * - Computing and caching recommendations by {date,currentGame,version}
 */

import { useMemo, useEffect } from 'react';
import { getRecommendedGame, getCurrentDate } from '../util';
import { useGameStatuses } from '../contexts/GameStatusesContext';
import { getAllGames } from '../games';

const RECOMMENDATION_LOOKBACK_DAYS = 30;
const RECOMMENDATION_SCOPE_KEY = 'recommendation_30d';

const recommendationCache = {
  version: null,
  values: new Map(),
};

/**
 * Generate dates array: current date + N-1 previous days (N total)
 */
const generateDatesForRecommendation = (currentDate, days = RECOMMENDATION_LOOKBACK_DAYS) => {
  const dates = [currentDate];
  const today = new Date(currentDate + 'T12:00:00');

  for (let i = 1; i < days; i += 1) {
    const prevDate = new Date(today);
    prevDate.setDate(today.getDate() - i);
    const year = prevDate.getFullYear();
    const month = String(prevDate.getMonth() + 1).padStart(2, '0');
    const day = String(prevDate.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
  }

  return dates;
};

const resolveEarliestRecommendationDate = (currentDate, days = RECOMMENDATION_LOOKBACK_DAYS) => {
  const date = new Date(`${currentDate}T12:00:00`);
  date.setDate(date.getDate() - (days - 1));
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Hook to get the recommended game for a user.
 *
 * @param {string} currentDate - Current date (defaults to getCurrentDate())
 * @param {string} currentGame - Optional current game to exclude from recommendations
 * @param {object} options
 * @param {boolean} options.subscribeOnly - Register scope but do not actively fetch
 * @param {boolean} options.allowBackgroundRevalidateOnSubscribe - If true, request one low-priority revalidate when cache is missing/hard stale
 * @param {'low'|'medium'|'high'} options.priority - Scope fetch priority
 * @returns {object} { recommendation, isLoading }
 */
export function useRecommendedGame(currentDate = null, currentGame = null, options = {}) {
  const {
    subscribeOnly = true,
    allowBackgroundRevalidateOnSubscribe = false,
    priority = 'medium',
  } = options;

  const date = currentDate || getCurrentDate();

  const gameData = useMemo(() => {
    const visibleGames = getAllGames().filter((game) => !game.hidden);
    const dates = generateDatesForRecommendation(date, RECOMMENDATION_LOOKBACK_DAYS);

    return visibleGames.map((gameConfig) => ({
      gameId: gameConfig.id,
      games: dates.map((gameDate) => ({ date: gameDate })),
    }));
  }, [date]);

  const {
    getGameStatus,
    isLoading,
    version,
    scopeState,
    requestScopeRefresh,
  } = useGameStatuses(gameData, true, {
    scopeKey: RECOMMENDATION_SCOPE_KEY,
    priority,
    subscribeOnly,
  });

  useEffect(() => {
    if (!subscribeOnly || !allowBackgroundRevalidateOnSubscribe) {
      return;
    }

    if (!scopeState.hasData || scopeState.isHardStale) {
      requestScopeRefresh({ priority: 'low', force: true });
    }
  }, [
    subscribeOnly,
    allowBackgroundRevalidateOnSubscribe,
    scopeState.hasData,
    scopeState.isHardStale,
    requestScopeRefresh,
  ]);

  const recommendation = useMemo(() => {
    if (isLoading) return null;

    if (recommendationCache.version !== version) {
      recommendationCache.version = version;
      recommendationCache.values = new Map();
    }

    const cacheKey = `${date}|${currentGame || ''}`;
    const cached = recommendationCache.values.get(cacheKey);
    if (cached) {
      return cached;
    }

    const earliestDate = resolveEarliestRecommendationDate(date, RECOMMENDATION_LOOKBACK_DAYS);

    const boundedServerStatus = (gameSlug, gameDate) => {
      // Keep recommendation scope bounded to the configured lookback window.
      // Outside the window, force "finished" to avoid expanding recommendation horizon.
      if (gameDate < earliestDate) {
        return 'finished';
      }
      return getGameStatus(gameSlug, gameDate);
    };

    const nextRecommendation = getRecommendedGame(currentGame, date, boundedServerStatus);
    recommendationCache.values.set(cacheKey, nextRecommendation);
    return nextRecommendation;
  }, [currentGame, date, getGameStatus, isLoading, version]);

  return {
    recommendation,
    isLoading,
  };
}

export default useRecommendedGame;
